import { useState, useCallback } from "react";
import { toast } from "react-toastify";

/**
 * useCurrentLocation フックの戻り値インターフェース
 */
export interface UseCurrentLocationReturn {
  /** 現在地の取得処理を実行する関数 */
  locate: (onSuccess?: (position: GeolocationPosition) => void) => void;
  /** 現在地取得中かどうかを示すフラグ */
  isLocating: boolean;
}

/**
 * ブラウザの Geolocation API をラップし、現在地取得とエラー時のトースト通知を提供するカスタムフック。
 *
 * @returns {UseCurrentLocationReturn} locate 関数と isLocating 状態を含むオブジェクト
 */
export const useCurrentLocation = (): UseCurrentLocationReturn => {
  const [isLocating, setIsLocating] = useState(false);

  /**
   * 現在地の取得を開始する。
   * 非対応環境や権限拒否・タイムアウト等のエラー発生時はトーストメッセージで通知する。
   *
   * @param onSuccess - 位置情報取得成功時に実行されるコールバック関数
   */
  const locate = useCallback(
    (onSuccess?: (position: GeolocationPosition) => void) => {
      if (!navigator?.geolocation) {
        toast.error(
          "お使いのブラウザまたは接続環境（HTTP）では現在地取得に対応していません"
        );
        return;
      }

      setIsLocating(true);

      /**
       * Permissions API を使って権限ダイアログ表示状態を確認した上で Geolocation API を呼び出す内部関数。
       */
      const requestLocation = async (): Promise<void> => {
        let timeout = 10000;
        if (navigator.permissions?.query) {
          try {
            const status = await navigator.permissions.query({
              name: "geolocation",
            });
            if (status.state === "prompt") {
              // 権限確認ダイアログの操作待ち時間を考慮し、最大60秒に設定
              timeout = 60000;
            }
          } catch {
            // Permissions API 非対応環境のフォールバック
          }
        }

        const options: PositionOptions = {
          enableHighAccuracy: true,
          timeout,
          maximumAge: 0,
        };

        navigator.geolocation.getCurrentPosition(
          (position) => {
            setIsLocating(false);
            if (onSuccess) {
              onSuccess(position);
            }
          },
          (error) => {
            setIsLocating(false);
            switch (error.code) {
              case error.PERMISSION_DENIED:
                toast.error(
                  "位置情報の利用が許可されていません。ブラウザの設定をご確認ください"
                );
                break;
              case error.POSITION_UNAVAILABLE:
                toast.error(
                  "現在地を取得できませんでした。電波状況やGPS設定をご確認ください"
                );
                break;
              case error.TIMEOUT:
                toast.error(
                  "現在地の取得がタイムアウトしました。時間を置いて再度お試しください"
                );
                break;
              default:
                toast.error("現在地を取得できませんでした");
                break;
            }
          },
          options
        );
      };

      void requestLocation();
    },
    []
  );

  return { locate, isLocating };
};
